/**
 * Helpers for deriving readable colours from tile background choices.
 * Keeps colour logic in one place so admin, preview and public portal all
 * behave identically.
 */

/**
 * Very small hex parser. Accepts "#RRGGBB", "#RGB", or "rgb(...)" strings.
 * Returns { r, g, b } in 0-255, or null for anything we can't parse.
 */
function parseColor(input) {
  if (!input || typeof input !== 'string') return null;
  const s = input.trim();
  if (s.startsWith('#')) {
    let hex = s.slice(1);
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return null;
    const n = parseInt(hex, 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
  }
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map(p => parseFloat(p.trim()));
    if (parts.length >= 3) return { r: parts[0], g: parts[1], b: parts[2] };
  }
  return null;
}

/**
 * Relative luminance (0..1) — WCAG spec.
 */
function luminance(rgb) {
  if (!rgb) return 1;
  const chan = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(rgb.r) + 0.7152 * chan(rgb.g) + 0.0722 * chan(rgb.b);
}

/**
 * Given a background colour, pick a readable foreground for icons.
 * Returns dark grey on light backgrounds, white on dark backgrounds.
 * Optionally caller can override.
 */
export function autoIconColor(backgroundColor, override) {
  if (override) return override;
  const rgb = parseColor(backgroundColor);
  return luminance(rgb) > 0.5 ? '#334155' : '#FFFFFF';
}
